import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, ChevronDown, Compass, Flame, Users } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { Pulse } from '@/components/shared/Pulse';
import { useTrailConnectPulse } from '@/features/marketplace/hooks/useTrailConnect';

/**
 * Mega-menu for the TrailConnect nav slot. Hover/focus to open on desktop,
 * click to toggle on touch. Surfaces both children (Community + Clubs)
 * editorially so the consolidation doesn't hurt discoverability.
 *
 * Surface + text colors use theme tokens so dark and light both render.
 */

type Child = {
  to: string;
  label: string;
  kicker: string;
  blurb: string;
  image: string;
  accent: string;
  icon: typeof Users;
  liveLine: string;
};

const OPEN_DELAY_MS = 90;
const CLOSE_DELAY_MS = 180;

export function TrailConnectNavMenu() {
  const [open, setOpen] = useState(false);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const { data: pulse } = useTrailConnectPulse();

  const children: Child[] = [
    {
      to: '/community',
      label: 'Community',
      kicker: 'Travel Crew',
      blurb: 'Match with companions for a trek. Join a trip group. Follow live expeditions.',
      image: photo.road,
      accent: brand.chinar,
      icon: Users,
      liveLine: pulse
        ? `${pulse.travelersOnline || 0} online · ${pulse.tripsFormingThisWeek || 0} trips forming`
        : 'Loading...',
    },
    {
      to: '/clubs',
      label: 'Clubs',
      kicker: 'Local Rituals',
      blurb: 'Recurring meet-ups — cycling at dawn, badminton at 7, yoga in Nishat at sunrise.',
      image: photo.meadow,
      accent: brand.teal,
      icon: Flame,
      liveLine: pulse
        ? `${pulse.activeClubs || 0} clubs · ${pulse.longestStreak || 0}-day top streak`
        : 'Loading...',
    },
  ];

  const isActive =
    pathname === '/trailconnect' ||
    pathname.startsWith('/trailconnect/') ||
    pathname === '/tribes' ||
    pathname === '/community' ||
    pathname.startsWith('/community/') ||
    pathname === '/clubs' ||
    pathname.startsWith('/clubs/');

  function clearTimers() {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }
  function scheduleOpen() {
    clearTimers();
    openTimer.current = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  }
  function scheduleClose() {
    clearTimers();
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }

  useEffect(() => () => clearTimers(), []);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
    >
      <Link
        to="/trailconnect"
        onFocus={scheduleOpen}
        onClick={(e) => {
          if (window.matchMedia('(hover: none)').matches && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-medium transition-colors',
          isActive ? 'bg-[var(--glass-hi)] text-ink' : 'text-ink-dim hover:text-ink',
        )}
      >
        TrailConnect
        <ChevronDown
          size={13}
          className={cn('transition-transform', open && 'rotate-180')}
        />
      </Link>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
            role="menu"
            className="absolute left-1/2 top-[calc(100%+10px)] z-50 w-[640px] -translate-x-1/2 rounded-3xl border p-3 backdrop-blur-2xl"
            style={{
              background: 'var(--popover)',
              borderColor: 'var(--glass-edge-hi)',
              boxShadow: 'var(--card-shadow)',
              color: 'var(--ink)',
            }}
          >
            <div className="grid grid-cols-2 gap-3">
              {children.map((c) => {
                const Icon = c.icon;
                return (
                  <Link
                    key={c.to}
                    to={c.to}
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className="group relative block overflow-hidden rounded-2xl border border-glass-edge bg-glass transition hover:bg-glass-hi"
                  >
                    <div className="relative h-28 overflow-hidden">
                      <img
                        src={unsplash(c.image, { w: 600 })}
                        alt=""
                        aria-hidden
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            'linear-gradient(180deg, rgba(0,0,0,0.1), rgba(0,0,0,0.7))',
                        }}
                      />
                      <div className="absolute left-3 top-3">
                        <span
                          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white"
                          style={{
                            background: `${c.accent}E0`,
                            boxShadow: `0 6px 18px ${c.accent}55`,
                          }}
                        >
                          <Icon size={11} />
                          {c.kicker}
                        </span>
                      </div>
                      <div className="absolute inset-x-3 bottom-2.5 flex items-center justify-between text-white">
                        <span className="font-display text-xl font-semibold">{c.label}</span>
                        <ArrowRight
                          size={14}
                          className="opacity-80 transition group-hover:translate-x-0.5 group-hover:opacity-100"
                        />
                      </div>
                    </div>
                    <div className="p-3.5">
                      <p className="text-[12.5px] leading-snug text-ink-dim">{c.blurb}</p>
                      <div
                        className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em]"
                        style={{ color: c.accent }}
                      >
                        <Pulse color={c.accent} size={5} />
                        {c.liveLine}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Quick links — stories + seasons */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              {[
                { to: '/stories', label: 'Trail Diaries', sub: 'Trip reports & guides' },
                { to: '/seasons', label: 'When to go', sub: 'Month-by-month Kashmir' },
              ].map(({ to, label, sub }) => (
                <Link
                  key={to}
                  to={to}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className="flex flex-col rounded-xl border border-glass-edge bg-glass px-3 py-2.5 transition hover:bg-glass-hi"
                >
                  <span className="text-[12px] font-semibold text-ink">{label}</span>
                  <span className="text-[10.5px] text-ink-mute">{sub}</span>
                </Link>
              ))}
            </div>

            <Link
              to="/trailconnect"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="mt-2 flex items-center justify-between rounded-2xl border border-glass-edge bg-glass px-4 py-3 transition hover:bg-glass-hi"
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="grid h-8 w-8 place-items-center rounded-full"
                  style={{
                    background: `linear-gradient(135deg, ${brand.teal}, ${brand.saffron})`,
                    boxShadow: `0 6px 20px ${brand.teal}55`,
                  }}
                >
                  <Compass size={14} className="text-white" />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-ink">
                    See how they connect
                  </div>
                  <div className="text-[11px] text-ink-dim">
                    The TrailConnect hub — flywheel, streaks, unified rewards
                  </div>
                </div>
              </div>
              <ArrowRight size={14} className="text-ink-dim" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
